import { useState, MouseEvent } from 'react';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FilterButton } from './FilterButton';
import {
  DatePreset,
  DateRange,
  PRESET_LABELS,
  resolvePreset,
} from './lib/dateRangePresets';

export interface DateRangeValue {
  preset: DatePreset;
  range: DateRange;
}

export interface DateRangePickerProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  /**
   * Forwarded to the trigger `FilterButton`'s `active` prop, which highlights
   * the trigger when the filter deviates from a page's default — a decision
   * that belongs to the page (e.g. COM-639), since only the page knows what
   * its own default preset/range is.
   *
   * Default: `value.preset !== 'custom' || Boolean(value.range.from || value.range.to)`
   * — i.e. "any non-custom preset, or any populated custom range, highlights
   * the trigger". This default is intentionally naive (it does not know a
   * page's default preset) and is kept only so existing/naive callers keep
   * their current look; it is NOT a considered UX rule — a page with a
   * non-`custom` default preset (e.g. "Last 30 days") should pass its own
   * `active` (typically `value.preset !== pageDefaultPreset`) rather than
   * rely on this default, or its trigger will render permanently highlighted.
   */
  active?: boolean;
}

const PRESET_ORDER: DatePreset[] = ['custom', 'last7days', 'last30days', 'last90days', 'last6months'];

export function DateRangePicker({ value, onChange, active }: DateRangePickerProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const openPopover = (e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const close = () => setAnchorEl(null);

  const pickPreset = (preset: DatePreset) => {
    if (preset === 'custom') {
      onChange({ preset, range: value.range });
      return; // popover stays open so From/To can be edited
    }
    onChange({ preset, range: resolvePreset(preset, new Date()) });
    close();
  };

  const setCustomEdge = (edge: 'from' | 'to', d: Dayjs | null) => {
    onChange({
      preset: 'custom',
      range: { ...value.range, [edge]: d ? d.toDate() : null },
    });
  };

  const label = value.preset === 'custom' ? 'Date' : PRESET_LABELS[value.preset];
  const isActive =
    active ?? (value.preset !== 'custom' || Boolean(value.range.from || value.range.to));

  return (
    <>
      <FilterButton label={label} active={isActive} onClick={openPopover} />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        // borderRadius/boxShadow come from the theme's MuiPopover.paper
        // override (ported from ComplyControl's adminPanelTheme.ts).
        PaperProps={{ sx: { p: 1, minWidth: 220 } }}
      >
        <MenuList dense>
          {PRESET_ORDER.map((preset) => (
            <MenuItem
              key={preset}
              selected={preset === value.preset}
              onClick={() => pickPreset(preset)}
              sx={{ fontSize: 14, borderRadius: '6px' }}
            >
              {PRESET_LABELS[preset]}
            </MenuItem>
          ))}
        </MenuList>
        {value.preset === 'custom' && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1 }}>
              <DatePicker
                label="From"
                value={value.range.from ? dayjs(value.range.from) : null}
                onChange={(d) => setCustomEdge('from', d)}
                slotProps={{ textField: { size: 'small' } }}
              />
              <DatePicker
                label="To"
                value={value.range.to ? dayjs(value.range.to) : null}
                onChange={(d) => setCustomEdge('to', d)}
                slotProps={{ textField: { size: 'small' } }}
              />
            </Box>
          </LocalizationProvider>
        )}
      </Popover>
    </>
  );
}
